import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Checkbox,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControlLabel,
  IconButton,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  useTheme,
  alpha,
  Stack,
  Tooltip,
  InputAdornment,
  Fade,
  Grid
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ClearIcon from '@mui/icons-material/Clear';
import SaveIcon from '@mui/icons-material/Save';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import ViewListIcon from '@mui/icons-material/ViewList';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SearchIcon from '@mui/icons-material/Search';
import { httpRequest } from '../../../../services/httpRequest';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux-store/appStore';

interface Department {
  DepartmentID: number;
  DepartmentName: string;
  Active: boolean;
  RowStatus: number;
  CreateDate: string;
  CreatedBy: number;
  UpdateDate: string | null;
  UpdatedBy: number | null;
  DeleteDate: string | null;
  DeletedBy: number | null;
  TownshipID: number;
}

interface ApiResponse {
  status: string;
  message: string;
  data: {
    totalRecords: number;
    currentPage: number;
    totalPages: number;
    data: Department[];
  };
}

const BidDepartments: React.FC = () => {
  const theme = useTheme();

  // State management
  const [departments, setDepartments] = useState<Department[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [newDepartment, setNewDepartment] = useState<string>('');
  const [isActive, setIsActive] = useState<boolean>(true);
  const [message, setMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedDepartment, setSelectedDepartment] =
    useState<Department | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  const userRole = userDetails.UserRole || '';

  // Debounce the search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Fetch departments
  const fetchDepartments = async () => {
    setTableLoading(true);
    try {
      const response = (await httpRequest(
        'GET',
        `/bid_portal/department/get_all?Page=${
          page + 1
        }&Size=${rowsPerPage}&Active=false${
          debouncedSearchTerm ? `&Department=${debouncedSearchTerm}` : ''
        }`
      )) as ApiResponse;

      if (response.status === 'success') {
        setDepartments(response.data.data);
        setTotalRecords(response.data.totalRecords);
      } else {
        setError(response.message || 'Failed to fetch departments');
      }
    } catch (err) {
      setError('Failed to fetch departments');
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    fetchDepartments();
  }, [page, rowsPerPage, debouncedSearchTerm]);

  // Handlers
  const handleDepartmentChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setNewDepartment(event.target.value);
  };

  const handleActiveChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsActive(event.target.checked);
  };

  const clearForm = () => {
    setNewDepartment('');
    setIsActive(true);
    setSelectedDepartment(null);
    setError('');
    setMessage('');
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!newDepartment.trim()) {
      setError('Department name cannot be blank.');
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        DepartmentName: newDepartment
      };

      if (selectedDepartment) {
        // Update existing department
        const updatePayload = {
          ...payload,
          Active: isActive
        };

        const response = (await httpRequest(
          'PATCH',
          `/bid_portal/department/update/${selectedDepartment.DepartmentID}`,
          updatePayload
        )) as ApiResponse;

        if (response.status === 'success') {
          clearForm();
          fetchDepartments();
          setMessage('Department updated successfully');
        } else {
          setError(response.message || 'Failed to update department');
        }
      } else {
        // Create new department
        const response = (await httpRequest(
          'POST',
          '/bid_portal/department/insert',
          payload
        )) as ApiResponse;

        if (response.status === 'success') {
          clearForm();
          fetchDepartments();
          setMessage('Department added successfully');
        } else {
          setError(response.message || 'Failed to add department');
        }
      }
    } catch (err) {
      setError('Failed to save department');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (department: Department) => {
    setSelectedDepartment(department);
    setNewDepartment(department.DepartmentName);
    setIsActive(department.Active);
    setError('');
    setMessage('');
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Helper function to format date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        bgcolor: alpha(theme.palette.background.default, 0.4),
        borderRadius: 2,
        minHeight: '100vh'
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 4 }}>
        <BusinessIcon
          sx={{
            fontSize: 32,
            color: theme.palette.primary.main
          }}
        />
        <Typography
          variant="h4"
          sx={{
            color: theme.palette.primary.main,
            fontWeight: 'bold'
          }}
        >
          Department Management
        </Typography>
      </Stack>

      {message && (
        <Fade in={!!message}>
          <Alert
            severity="success"
            sx={{
              mb: 3,
              boxShadow: 2,
              borderRadius: 2
            }}
            onClose={() => setMessage('')}
          >
            {message}
          </Alert>
        </Fade>
      )}

      {error && (
        <Fade in={!!error}>
          <Alert
            severity="error"
            sx={{
              mb: 3,
              boxShadow: 2,
              borderRadius: 2
            }}
            onClose={() => setError('')}
          >
            {error}
          </Alert>
        </Fade>
      )}

      {userRole === 'Admin' && (
        <Card
          elevation={2}
          sx={{
            mb: 4,
            borderRadius: 2,
            overflow: 'visible'
          }}
        >
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {selectedDepartment ? (
                  <EditIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                ) : (
                  <AddCircleOutlineIcon
                    sx={{ mr: 1, color: theme.palette.primary.main }}
                  />
                )}
                <Typography variant="h6">
                  {selectedDepartment
                    ? 'Edit Department'
                    : 'Create New Department'}
                </Typography>
                {selectedDepartment && (
                  <Chip
                    label={`ID: ${selectedDepartment.DepartmentID}`}
                    size="small"
                    sx={{
                      ml: 2,
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      color: theme.palette.info.main
                    }}
                  />
                )}
              </Box>
            }
            sx={{
              bgcolor: alpha(theme.palette.primary.light, 0.1),
              pb: 1
            }}
          />
          <CardContent sx={{ p: 3 }}>
            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={6}>
                  <TextField
                    required
                    label="Department Name"
                    value={newDepartment}
                    onChange={handleDepartmentChange}
                    error={!!error}
                    helperText={error ? error : ''}
                    fullWidth
                    InputProps={{
                      sx: { borderRadius: 1 },
                      startAdornment: (
                        <InputAdornment position="start">
                          <BusinessIcon
                            sx={{ color: theme.palette.primary.main }}
                          />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>

                {selectedDepartment && (
                  <Grid item xs={12} md={4}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        backgroundColor: alpha(
                          isActive
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                          0.05
                        ),
                        border: `1px solid ${alpha(
                          isActive
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                          0.2
                        )}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {isActive ? (
                          <CheckCircleOutlineIcon
                            sx={{ color: theme.palette.success.main, mr: 1 }}
                          />
                        ) : (
                          <HighlightOffIcon
                            sx={{ color: theme.palette.error.main, mr: 1 }}
                          />
                        )}
                        <Typography
                          variant="body1"
                          sx={{
                            color: isActive
                              ? theme.palette.success.main
                              : theme.palette.error.main,
                            fontWeight: 500
                          }}
                        >
                          {isActive ? 'Active' : 'Inactive'}
                        </Typography>
                      </Box>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isActive}
                            onChange={handleActiveChange}
                            color={isActive ? 'success' : 'error'}
                          />
                        }
                        label=""
                        sx={{ m: 0 }}
                      />
                    </Paper>
                  </Grid>
                )}

                <Grid item xs={12} md={selectedDepartment ? 2 : 6}>
                  <Stack
                    direction="row"
                    spacing={2}
                    justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                  >
                    <Button
                      variant="outlined"
                      onClick={clearForm}
                      startIcon={<ClearIcon />}
                      sx={{
                        borderRadius: 1,
                        px: 2
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isLoading}
                      startIcon={
                        isLoading ? (
                          <CircularProgress size={20} />
                        ) : selectedDepartment ? (
                          <SaveIcon />
                        ) : (
                          <AddCircleOutlineIcon />
                        )
                      }
                      sx={{
                        borderRadius: 1,
                        px: 3
                      }}
                    >
                      {isLoading
                        ? 'Saving...'
                        : selectedDepartment
                        ? 'Update'
                        : 'Create'}
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Table Section */}
      <Card elevation={2} sx={{ borderRadius: 2 }}>
        <CardHeader
          title={
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ViewListIcon
                  sx={{ mr: 1, color: theme.palette.primary.main }}
                />
                <Typography variant="h6">All Departments</Typography>
                <Chip
                  label={`${totalRecords} total`}
                  size="small"
                  sx={{
                    ml: 2,
                    bgcolor: alpha(theme.palette.primary.light, 0.1),
                    color: theme.palette.primary.main
                  }}
                />
              </Box>

              <TextField
                placeholder="Search departments..."
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="primary" />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchTerm('')}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: 1,
                    bgcolor: alpha(theme.palette.background.default, 0.7)
                  }
                }}
                sx={{ width: { xs: '100%', sm: 250 } }}
              />
            </Box>
          }
          sx={{
            bgcolor: alpha(theme.palette.background.default, 0.5),
            borderBottom: `1px solid ${theme.palette.divider}`
          }}
        />

        <TableContainer sx={{ maxHeight: 650 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    bgcolor: theme.palette.background.paper,
                    py: 2
                  }}
                >
                  Department Name
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    bgcolor: theme.palette.background.paper
                  }}
                >
                  Status
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    bgcolor: theme.palette.background.paper
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarTodayIcon sx={{ mr: 1, fontSize: 18 }} />
                    Created Date
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    bgcolor: theme.palette.background.paper
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarTodayIcon sx={{ mr: 1, fontSize: 18 }} />
                    Updated Date
                  </Box>
                </TableCell>
                {userRole === 'Admin' && (
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      bgcolor: theme.palette.background.paper
                    }}
                  >
                    Actions
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {tableLoading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={30} />
                    <Typography
                      variant="body2"
                      sx={{ mt: 2, color: theme.palette.text.secondary }}
                    >
                      Loading departments...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : departments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      {searchTerm
                        ? `No departments matching "${searchTerm}" found`
                        : 'No departments found'}
                    </Typography>
                    {searchTerm && (
                      <Button
                        variant="text"
                        color="primary"
                        startIcon={<ClearIcon />}
                        onClick={() => setSearchTerm('')}
                        sx={{ mt: 1 }}
                      >
                        Clear Search
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                departments.map((department) => (
                  <TableRow
                    key={department.DepartmentID}
                    sx={{
                      '&:nth-of-type(odd)': {
                        bgcolor: alpha(theme.palette.background.default, 0.3)
                      },
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.light, 0.05)
                      }
                    }}
                  >
                    <TableCell sx={{ py: 2, fontWeight: 'medium' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <BusinessIcon
                          sx={{
                            mr: 1,
                            color: theme.palette.primary.main,
                            fontSize: 18
                          }}
                        />
                        {department.DepartmentName}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={department.Active ? 'Active' : 'Inactive'}
                        icon={
                          department.Active ? (
                            <CheckCircleOutlineIcon />
                          ) : (
                            <HighlightOffIcon />
                          )
                        }
                        sx={{
                          backgroundColor: department.Active
                            ? alpha(theme.palette.success.main, 0.1)
                            : alpha(theme.palette.error.main, 0.1),
                          color: department.Active
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                          fontWeight: 500,
                          borderRadius: 1,
                          '& .MuiChip-icon': {
                            color: 'inherit'
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={department.CreateDate} arrow>
                        <Typography variant="body2">
                          {formatDate(department.CreateDate)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={department.UpdateDate || ''} arrow>
                        <Typography variant="body2">
                          {formatDate(department.UpdateDate)}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    {userRole === 'Admin' && (
                      <TableCell align="center">
                        <Tooltip title="Edit Department" arrow>
                          <Button
                            onClick={() => handleEdit(department)}
                            size="small"
                            variant="text"
                            color="primary"
                            startIcon={<EditIcon />}
                            sx={{
                              borderRadius: 1,
                              '&:hover': {
                                backgroundColor: alpha(
                                  theme.palette.primary.main,
                                  0.04
                                )
                              }
                            }}
                          >
                            Edit
                          </Button>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Divider />
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          component="div"
          count={totalRecords}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: `1px solid ${theme.palette.divider}`,
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
              {
                my: 1
              }
          }}
        />
      </Card>

      {isLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: alpha(theme.palette.background.default, 0.7),
            backdropFilter: 'blur(4px)',
            zIndex: 9999
          }}
        >
          <Paper
            elevation={4}
            sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: 2
            }}
          >
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="subtitle1">
              {selectedDepartment
                ? 'Updating department...'
                : 'Creating department...'}
            </Typography>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default BidDepartments;
